
import { ofType } from "redux-observable";
import { filter, mergeMap, catchError, map, debounceTime } from "rxjs/operators";
import { of, EMPTY, fromEvent, merge, timer } from "rxjs";
import { ajax } from "rxjs/ajax";
import { getBaseUrl } from "frame/base-url";
import {
	networkChanged,
	checkNetworkConnection,
	sendRequested,
} from "../actions";
import fromTypes from "../actions/types";
import frameTypes from "frame/actions/types";

/* triggers on user activity */
export const onlineStatusPollEffect = (action$, state$) => {
	return action$.pipe(
		filter(
			(action) =>
				action.type.indexOf("_CLICKED") !== -1 || // on any form save
				action.type === "@@router/LOCATION_CHANGE" // on user navigation
		),
		mergeMap(() => of(checkNetworkConnection()))
	);
};

export const checkNetworkEffect = (action$, state$) =>
	action$.pipe(
		ofType(fromTypes.CHECK_NETWORK_CONNECTION),
		mergeMap(() =>
			ajax({
				url: `${getBaseUrl()}/api/online`,
				method: "HEAD",
			}).pipe(
                mergeMap(() => {
                    const hasNetworkFlag = state$.value.queue.hasNetwork;
                    const hasPending = !!state$.value.queue.list && state$.value.queue.list.length > 0;
                    if (!hasNetworkFlag) {
                        return of(networkChanged(true), sendRequested());
                    }
                    if (hasPending) {
                        return of(sendRequested());
                    }
                    return EMPTY;
                }),
				catchError(() => {
					// Ignore transient reachability blips — only mark offline if we still
					// look offline after a short grace period (mobile radios flap often).
					if (!state$.value.queue.hasNetwork) {
						return EMPTY;
					}
					return timer(2000).pipe(
						mergeMap(() => {
							if (!state$.value.queue.hasNetwork) {
								return EMPTY;
							}
							// Re-check once before flipping offline
							return ajax({
								url: `${getBaseUrl()}/api/online`,
								method: "HEAD",
							}).pipe(
								mergeMap(() => EMPTY),
								catchError(() => {
									if (state$.value.queue.hasNetwork) {
										return of(networkChanged(false));
									}
									return EMPTY;
								})
							);
						})
					);
				})
			)
		)
	);

// React to browser-level online/offline events.
// Debounce offline so brief cellular blips don't flicker the whole UI / GPS chrome.
export const browserOnlineOfflineEffect = (action$, state$) => {
	const onOnline$ = fromEvent(window, "online").pipe(
		mergeMap(() => {
			const hasPending = !!state$.value.queue?.list && state$.value.queue.list.length > 0;
			if (hasPending) {
				return of(networkChanged(true), sendRequested());
			}
			return of(networkChanged(true));
		})
	);

	const onOffline$ = fromEvent(window, "offline").pipe(
		debounceTime(2000),
		filter(() => {
			// Only go offline if the browser still reports offline after the grace period
			const browserOnline =
				typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
					? navigator.onLine
					: false;
			return !browserOnline && !!state$.value.queue?.hasNetwork;
		}),
		map(() => networkChanged(false))
	);

	return merge(onOnline$, onOffline$);
};

// Set initial network state on app rehydrate/initialise
export const setInitialNetworkEffect = (action$) =>
	action$.pipe(
		ofType("persist/REHYDRATE", frameTypes.FRAME_INITIALISE_REQUESTED),
		mergeMap(() => {
			const online = (typeof navigator !== "undefined" && typeof navigator.onLine === "boolean") ? navigator.onLine : false;
			if (online) {
				// Mark online immediately and kick a backend reachability check
				return of(networkChanged(true), checkNetworkConnection());
			}
			return of(networkChanged(false));
		})
	);
